import _, { reduce } from "lodash";
import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useState,
} from "react";
import UserAPI from "restAPI/UserAPI";
import { FilterOption } from "types/pagination";

interface User {
  id: number;
  email: string;
  displayName: string;
}

interface UserDataMap {
  [key: string]: any;
}

interface GlobalUserListContextType {
  getUserName: (email?: string) => string;
  getUserData: (key: FilterOption) => Partial<User>;
  getNameList: (list: string[]) => { id: string; name: string }[];
  getAllUsersList: () => User[];
  isLoading: boolean;
  userDetails?: UserDataMap;
  refreshUsers: () => void;
}

const defaultContext: GlobalUserListContextType = {
  getUserName: (email?: string) => email || "",
  getUserData: () => ({}),
  getNameList: (list: string[]) =>
    list.map((email) => ({ id: email, name: email })),
  getAllUsersList: () => [],
  isLoading: false,
  userDetails: {},
  refreshUsers: () => {},
};

const GlobalUserListContext =
  createContext<GlobalUserListContextType>(defaultContext);

const Actions = {
  SET_USER_DATA: "SET_USER_DATA",
};

let GLOBAL_USER_LIST: {
  userData?: UserDataMap;
  allUsers?: User[];
} = {};

type Action = {
  type: string;
  payload: User[];
};

function reducer(state: typeof GLOBAL_USER_LIST, { type, payload }: Action) {
  switch (type) {
    case Actions.SET_USER_DATA: {
      const _users = reduce(
        payload,
        (acc: UserDataMap, user: User) => {
          const { id, email, displayName } = user;
          return {
            ...acc,
            [email]: displayName,
            [id]: { email, displayName },
          };
        },
        {},
      );

      GLOBAL_USER_LIST = {
        ...state,
        userData: _users,
        allUsers: payload,
      };
      return { ...GLOBAL_USER_LIST };
    }
    default:
      return state;
  }
}

interface Props {
  children: ReactNode;
}

function GlobalUserListContextProvider({ children }: Props) {
  const [state, dispatch] = useReducer(reducer, GLOBAL_USER_LIST);
  const [isLoading, setLoading] = useState(true);

  const userDetails = state.userData;
  const allUsers = state.allUsers;

  const fetchUserList = useCallback(() => {
    setLoading(true);
    const { promise, cancel } = UserAPI.getAll();

    promise
      .then((data: User[]) => {
        if (!_.isEmpty(data)) {
          dispatch({
            type: Actions.SET_USER_DATA,
            payload: data,
          });
        }
      })
      .catch((e) => console.error("Error in fetchUsers:", e))
      .finally(() => setLoading(false));

    return cancel;
  }, []);

  useEffect(() => {
    if (!_.isEmpty(userDetails)) {
      setLoading(false);
      return;
    }
    fetchUserList();
  }, [fetchUserList, userDetails]);

  const getUserName = useCallback(
    (email: string | undefined): string => {
      if (!email) return "";
      return userDetails?.[email] || email;
    },
    [userDetails],
  );

  const getUserData = useCallback(
    (key: FilterOption): Partial<User> => {
      const id =
        typeof key === "object" && key !== null && "id" in key ? key.id : key;

      return (id !== undefined && userDetails?.[id as string | number]) || {};
    },
    [userDetails],
  );

  const getNameList = useCallback(
    (list: string[]) => {
      return _.map(list, (email) => ({
        id: email,
        name: userDetails?.[email] || email,
      }));
    },
    [userDetails],
  );

  const getAllUsersList = useCallback(() => {
    return allUsers ? [...allUsers] : [];
  }, [allUsers]);

  return (
    <GlobalUserListContext.Provider
      value={{
        getUserName,
        getNameList,
        getUserData,
        isLoading,
        userDetails,
        refreshUsers: fetchUserList,
        getAllUsersList,
      }}
    >
      {children}
    </GlobalUserListContext.Provider>
  );
}

export default GlobalUserListContextProvider;

export function withGlobalUserListContext<P>(
  Component: React.ComponentType<
    P & { globalUserListContext: GlobalUserListContextType }
  >,
) {
  return (props: P) => (
    <GlobalUserListContext.Consumer>
      {(context) => <Component globalUserListContext={context} {...props} />}
    </GlobalUserListContext.Consumer>
  );
}

export function useGlobalUserListContext() {
  return useContext(GlobalUserListContext);
}
